import { useToast } from '@lifebit-ai/lds'
import { QueryKey, useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'

import { ColumnForExport, convertToReadableData, downloadDataAsFile, jsonArrayToCsv } from 'utils/export'

export const EXPORT_ERROR_MSG = 'Error exporting data.'
export const EXPORT_EMPTY_MSG = 'There is no data to export.'

const FIRST_EXPORT_PAGE = 1

/** `getPageItems` for the `{ data, pagination }` shape most of our endpoints return. */
export const getPaginatedItems = <TItem>(page: { data: TItem[] }): TItem[] => page.data

/** `getNextPage` for the `{ data, pagination }` shape most of our endpoints return. */
export const getNextPaginatedPage = (page: { pagination: PaginationMeta }): number | undefined => {
  const { currentPage, totalPages } = page.pagination
  return currentPage < totalPages ? currentPage + 1 : undefined
}

interface UseExportToCSVParams<TItem extends object, TPage> {
  /** Cache key of the paginated request. Include the filters so each set of data is exported on its own. */
  queryKey: QueryKey
  /** Fetches a single page. The first page requested is page 1. */
  fetchPage: (page: number, signal?: AbortSignal) => Promise<TPage>
  /** The items of a page that should end up in the file. */
  getPageItems: (page: TPage) => TItem[]
  /** The number of the page following `lastPage`, or undefined once the last one was fetched. */
  getNextPage: (lastPage: TPage) => number | undefined
  /** Headers of the file and how each item renders under them. */
  columns: ColumnForExport<TItem>[]
  /** Name of the downloaded file, extension included. */
  fileName: string
  errorMessage?: string
  emptyMessage?: string
}

interface UseExportToCSVResult {
  exportToCSV: () => void
  isExporting: boolean
}

/**
 * Turns a paginated endpoint into a CSV download: once triggered it walks through every
 * page, converts all the items at once and downloads the result. Nothing is requested
 * until `exportToCSV` is called.
 */
export const useExportToCSV = <TItem extends object, TPage>({
  queryKey,
  fetchPage,
  getPageItems,
  getNextPage,
  columns,
  fileName,
  errorMessage = EXPORT_ERROR_MSG,
  emptyMessage = EXPORT_EMPTY_MSG
}: UseExportToCSVParams<TItem, TPage>): UseExportToCSVResult => {
  const queryClient = useQueryClient()
  const [isExporting, setIsExporting] = useState(false)
  const toast = useToast()

  // The paging loop below is driven by the query state alone, so it reads the rest of its
  // inputs from here. That keeps callers free to pass inline columns and callbacks without
  // a re-render restarting an export half way through.
  const latestInputs = useRef({ fetchPage, getPageItems, getNextPage, columns, fileName, errorMessage, emptyMessage })
  latestInputs.current = { fetchPage, getPageItems, getNextPage, columns, fileName, errorMessage, emptyMessage }

  const { data, isSuccess, isError, hasNextPage, isFetchingNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey,
    initialPageParam: FIRST_EXPORT_PAGE,
    queryFn: ({ pageParam, signal }) => latestInputs.current.fetchPage(pageParam, signal),
    getNextPageParam: (lastPage, pages) => {
      const nextPage = latestInputs.current.getNextPage(lastPage)

      // Pages are fetched one after another, so the next one always has to come after the
      // ones already downloaded. An endpoint that keeps reporting the same page would
      // otherwise leave the loop below appending it forever.
      return nextPage !== undefined && nextPage > pages.length ? nextPage : undefined
    },
    enabled: isExporting,
    // An export is a one-off action: surface the failure instead of silently retrying.
    retry: false
  })

  const downloadedPages = data?.pages.length ?? 0
  const isDownloadingPages = isExporting && (!isSuccess || hasNextPage === true || isFetchingNextPage)

  // Keep pulling pages until the endpoint says there are no more. `downloadedPages` is part
  // of the dependencies because a page can resolve without `isFetchingNextPage` ever being
  // committed to a render, which would otherwise stall the loop.
  useEffect(() => {
    if (!isExporting || !isSuccess || !hasNextPage || isFetchingNextPage) return

    fetchNextPage()
  }, [isExporting, isSuccess, hasNextPage, isFetchingNextPage, downloadedPages, fetchNextPage])

  // Every page has arrived: build the CSV out of all of them and download it.
  useEffect(() => {
    if (!isExporting || !isSuccess || hasNextPage || isFetchingNextPage || !data) return

    const { getPageItems: getItems, columns: fileColumns, fileName: name } = latestInputs.current

    try {
      const items = data.pages.flatMap(getItems)

      if (!items.length) {
        toast({ message: latestInputs.current.emptyMessage })
        return
      }

      downloadDataAsFile(jsonArrayToCsv(convertToReadableData(items, fileColumns)), name, 'text/csv')
    } catch (err) {
      toast({ message: latestInputs.current.errorMessage })
    } finally {
      setIsExporting(false)
    }
  }, [isExporting, isSuccess, hasNextPage, isFetchingNextPage, data, toast])

  useEffect(() => {
    if (!isError) return

    toast({ message: latestInputs.current.errorMessage })
    setIsExporting(false)
  }, [isError, toast])

  const exportToCSV = useCallback(() => {
    // Drop the pages downloaded by a previous export so the file always holds fresh data.
    queryClient.removeQueries({ queryKey, exact: true })
    setIsExporting(true)
  }, [queryClient, queryKey])

  return { exportToCSV, isExporting: isDownloadingPages }
}
